import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Divider,
  Tooltip,
  Paper,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  MdEdit,
  MdDelete,
  MdExpandMore,
  MdExpandLess,
  MdOutlineQueryStats,
  MdSportsScore,
  MdInfo,
  MdAccessTime,
  MdTrendingUp,
  MdCalculate,
  MdSave,
  MdClose,
  MdSportsSoccer,
  MdCheck,
  MdCancel,
  MdTimer,
  MdStadium
} from 'react-icons/md';

interface SmartTicket {
  id: string;
  name: string;
  type: 'conservative' | 'balanced' | 'aggressive' | 'custom';
  opportunities: any[];
  totalOdds: number;
  expectedReturn: number;
  stake: number;
  potentialProfit: number;
  confidenceScore: number;
  riskLevel: 'Baixo' | 'Médio' | 'Alto';
  createdAt: string;
  status: 'active' | 'won' | 'lost' | 'cancelled';
  reasoning: string;
  calculations: {
    winProbability: number;
    expectedValue: number;
    sharpeRatio: number;
    kellyPercentage: number;
  };
}

interface TicketCardProps {
  ticket: SmartTicket;
  onEdit?: (ticketId: string, updatedData: Partial<SmartTicket>) => void;
  onDelete?: (ticketId: string) => void;
  onViewDetails?: (ticketId: string) => void;
}

export default function TicketCard({ 
  ticket, 
  onEdit, 
  onDelete, 
  onViewDetails 
}: TicketCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editedTicket, setEditedTicket] = useState({
    name: ticket.name,
    stake: ticket.stake
  });

  const handleEdit = () => {
    if (onEdit) {
      onEdit(ticket.id, {
        name: editedTicket.name,
        stake: editedTicket.stake,
        expectedReturn: editedTicket.stake * ticket.totalOdds,
        potentialProfit: (editedTicket.stake * ticket.totalOdds) - editedTicket.stake
      });
    }
    setEditDialogOpen(false);
  };

  const handleViewDetails = () => {
    setDetailsDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return { bg: '#1d3a36', color: '#49fbb0', label: '🟢 ATIVO' };
      case 'won': return { bg: '#2e7d32', color: 'white', label: '✅ GANHO' };
      case 'lost': return { bg: '#d32f2f', color: 'white', label: '❌ PERDIDO' };
      default: return { bg: '#757575', color: 'white', label: '⏸️ CANCELADO' };
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 70) return { color: '#4caf50', bg: 'rgba(76, 175, 80, 0.1)' };
    if (score >= 60) return { color: '#ffa726', bg: 'rgba(255, 167, 38, 0.1)' };
    return { color: '#f44336', bg: 'rgba(244, 67, 54, 0.1)' };
  };

  const statusConfig = getStatusColor(ticket.status);
  const confidenceConfig = getConfidenceColor(ticket.confidenceScore);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'won': return <MdCheck style={{ color: '#4caf50' }} />;
      case 'lost': return <MdCancel style={{ color: '#f44336' }} />;
      case 'cancelled': return <MdTimer style={{ color: '#757575' }} />;
      default: return <MdSportsSoccer style={{ color: '#49fbb0' }} />;
    }
  };

  return (
    <>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, rgba(29, 58, 54, 0.9) 0%, rgba(24, 53, 48, 0.95) 100%)',
          border: '1px solid rgba(73, 251, 176, 0.25)',
          borderRadius: '12px',
          overflow: 'hidden',
          position: 'relative',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(73, 251, 176, 0.2)',
            borderColor: 'rgba(73, 251, 176, 0.5)'
          }
        }}
      >
        {/* Header Premium */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #49fbb0 0%, #36d399 100%)',
          p: 1.5,
          position: 'relative'
        }}>
          {/* Background Pattern */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            borderRadius: '12px 12px 0 0'
          }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
            <Box sx={{ flex: 1, mr: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#183530', 
                  fontWeight: 800, 
                  mb: 0.5,
                  fontSize: '1rem',
                  letterSpacing: '-0.5px'
                }}
              >
                {ticket.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#1d3a36', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                >
                  <MdAccessTime size={12} />
                  {new Date(ticket.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
                
                <Chip
                  label={ticket.type.toUpperCase()}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(24, 53, 48, 0.8)',
                    color: '#49fbb0',
                    border: '1px solid rgba(73, 251, 176, 0.4)',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: '20px'
                  }}
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={statusConfig.label}
                sx={{
                  backgroundColor: statusConfig.bg,
                  color: statusConfig.color,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: '24px',
                  border: 'none'
                }}
              />
              
              <Tooltip title="Editar Bilhete">
                <IconButton
                  onClick={() => setEditDialogOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(24, 53, 48, 0.8)',
                    color: '#49fbb0',
                    width: 28,
                    height: 28,
                    '&:hover': {
                      backgroundColor: 'rgba(24, 53, 48, 1)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <MdEdit size={14} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Métricas Principais - 3 Colunas Harmonizadas */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {/* STAKE */}
            <Grid item xs={4}>
              <Paper 
                elevation={0}
                sx={{ 
                  textAlign: 'center',
                  p: 1.5,
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#ffffff', 
                    fontWeight: 700, 
                    display: 'block', 
                    mb: 0.5,
                    fontSize: '0.65rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  STAKE
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 800,
                    fontSize: '1rem',
                    textShadow: '0 2px 8px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {new Intl.NumberFormat('pt-BR', { 
                    style: 'currency', 
                    currency: 'BRL' 
                  }).format(ticket.stake)}
                </Typography>
              </Paper>
            </Grid>
            
            {/* RETORNO POTENCIAL */}
            <Grid item xs={4}>
              <Paper 
                elevation={0}
                sx={{ 
                  textAlign: 'center',
                  p: 1.5,
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(76, 175, 80, 0.03) 100%)',
                  borderRadius: '8px',
                  border: '1px solid rgba(76, 175, 80, 0.25)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#4caf50', 
                    fontWeight: 700, 
                    display: 'block', 
                    mb: 0.5,
                    fontSize: '0.65rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  RETORNO
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#4caf50', 
                    fontWeight: 800,
                    fontSize: '1rem',
                    textShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                  }}
                >
                  {new Intl.NumberFormat('pt-BR', { 
                    style: 'currency', 
                    currency: 'BRL' 
                  }).format(ticket.expectedReturn)}
                </Typography>
              </Paper>
            </Grid>
            
            {/* CONFIANÇA */}
            <Grid item xs={4}>
              <Paper 
                elevation={0}
                sx={{ 
                  textAlign: 'center',
                  p: 1.5,
                  background: `linear-gradient(135deg, ${confidenceConfig.bg.replace('0.1', '0.12')} 0%, transparent 100%)`,
                  borderRadius: '8px',
                  border: `1px solid ${confidenceConfig.color}40`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: confidenceConfig.color, 
                    fontWeight: 700, 
                    display: 'block', 
                    mb: 0.5,
                    fontSize: '0.65rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  CONFIANÇA
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: confidenceConfig.color, 
                    fontWeight: 800,
                    fontSize: '1rem',
                    textShadow: `0 2px 8px ${confidenceConfig.color}30`
                  }}
                >
                  {ticket.confidenceScore}%
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Seleções Preview */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#49fbb0', 
                  fontWeight: 700, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  fontSize: '0.75rem'
                }}
              >
                <MdSportsScore size={14} />
                SELEÇÕES ({ticket.opportunities.length})
              </Typography>
              
              <IconButton
                onClick={() => setExpanded(!expanded)}
                sx={{ 
                  color: '#49fbb0',
                  padding: 0.5
                }}
              >
                {expanded ? <MdExpandLess size={16} /> : <MdExpandMore size={16} />}
              </IconButton>
            </Box>
            
            {/* Preview das primeiras 2 seleções */}
            {!expanded && (
              <Stack spacing={0.75}>
                {ticket.opportunities.slice(0, 2).map((opp, index) => (
                  <Box 
                    key={opp.id}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.75rem' }}>
                        {opp.homeTeamName} vs {opp.awayTeamName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                        {opp.betType} • {opp.betOption}
                      </Typography>
                    </Box>
                    
                    <Chip
                      label={typeof opp.odds === 'string' ? parseFloat(opp.odds).toFixed(2) : opp.odds.toFixed(2)}
                      size="small"
                      sx={{
                        backgroundColor: '#49fbb0',
                        color: '#183530',
                        fontWeight: 700,
                        minWidth: '45px',
                        height: '20px',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                ))}
                
                {ticket.opportunities.length > 2 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary', 
                      textAlign: 'center',
                      fontStyle: 'italic',
                      fontSize: '0.65rem',
                      mt: 0.5
                    }}
                  >
                    +{ticket.opportunities.length - 2} seleções adicionais
                  </Typography>
                )}
              </Stack>
            )}

            {/* Expanded view */}
            <Collapse in={expanded}>
              <Stack spacing={0.75}>
                {ticket.opportunities.map((opp, index) => (
                  <Box 
                    key={opp.id}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.75rem', mb: 0.25 }}>
                        {opp.homeTeamName} vs {opp.awayTeamName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                          {opp.betType} • {opp.betOption}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#49fbb0', fontSize: '0.65rem' }}>
                          Confiança: {opp.confidenceScore}%
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Chip
                      label={typeof opp.odds === 'string' ? parseFloat(opp.odds).toFixed(2) : opp.odds.toFixed(2)}
                      sx={{
                        backgroundColor: '#49fbb0',
                        color: '#183530',
                        fontWeight: 700,
                        minWidth: '45px',
                        height: '20px',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
            <Button 
              variant="contained"
              startIcon={<MdOutlineQueryStats size={16} />}
              onClick={handleViewDetails}
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #49fbb0 0%, #36d399 100%)',
                color: '#183530',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                py: 1,
                fontSize: '0.75rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #36d399 0%, #2dd4aa 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(73, 251, 176, 0.4)'
                }
              }}
            >
              Ver Detalhes
            </Button>
            
            <Tooltip title="Remover Bilhete">
              <IconButton
                onClick={() => onDelete?.(ticket.id)}
                sx={{
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  color: '#f44336',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  width: 35,
                  height: 35,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <MdDelete size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog de Edição */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(29, 58, 54, 0.95) 0%, rgba(24, 53, 48, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(73, 251, 176, 0.2)',
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#49fbb0', 
          fontWeight: 700,
          borderBottom: '1px solid rgba(73, 251, 176, 0.2)',
          p: 2,
          fontSize: '1rem'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MdEdit size={16} />
            Editar Bilhete
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Nome do Bilhete"
              fullWidth
              value={editedTicket.name}
              onChange={(e) => setEditedTicket(prev => ({ ...prev, name: e.target.value }))}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(73, 251, 176, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(73, 251, 176, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#49fbb0',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#49fbb0',
                  fontSize: '0.85rem',
                  '&.Mui-focused': {
                    color: '#49fbb0',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                  fontSize: '0.85rem',
                },
              }}
            />

            <TextField
              label="Stake"
              type="number"
              fullWidth
              value={editedTicket.stake}
              onChange={(e) => setEditedTicket(prev => ({ ...prev, stake: parseFloat(e.target.value) || 0 }))}
              size="small"
              InputProps={{
                startAdornment: 'R$',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(73, 251, 176, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(73, 251, 176, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#49fbb0',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#49fbb0',
                  fontSize: '0.85rem',
                  '&.Mui-focused': {
                    color: '#49fbb0',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                  fontSize: '0.85rem',
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{
              color: '#49fbb0',
              borderColor: 'rgba(73, 251, 176, 0.3)',
              fontSize: '0.75rem',
              '&:hover': {
                borderColor: '#49fbb0',
                backgroundColor: 'rgba(73, 251, 176, 0.08)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            sx={{
              background: 'linear-gradient(135deg, #49fbb0 0%, #36d399 100%)',
              color: '#183530',
              fontWeight: 700,
              fontSize: '0.75rem',
              '&:hover': {
                background: 'linear-gradient(135deg, #36d399 0%, #2dd4aa 100%)',
              },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Detalhes */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(29, 58, 54, 0.95) 0%, rgba(24, 53, 48, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(73, 251, 176, 0.2)',
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2,
          borderBottom: '1px solid rgba(73, 251, 176, 0.2)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ 
              color: '#49fbb0',
              fontWeight: 700,
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <MdSportsScore size={18} />
              Detalhes do Bilhete
            </Typography>
            <Chip
              label={statusConfig.label}
              sx={{
                backgroundColor: statusConfig.bg,
                color: statusConfig.color,
                fontWeight: 700,
                fontSize: '0.7rem',
                height: '24px'
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Informações Básicas */}
            <Box sx={{ 
              p: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Data de Criação
                  </Typography>
                  <Typography sx={{ color: 'white', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MdAccessTime size={14} />
                    {new Date(ticket.createdAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Tipo de Bilhete
                  </Typography>
                  <Typography sx={{ color: '#49fbb0', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>
                    {ticket.type}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Lista de Apostas */}
            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#49fbb0',
                  mb: 1,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <MdStadium size={14} />
                APOSTAS SELECIONADAS
              </Typography>

              <Stack spacing={1}>
                {ticket.opportunities.map((opp, index) => (
                  <Paper
                    key={opp.id}
                    sx={{
                      p: 1.5,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 600 }}>
                        {opp.homeTeamName} vs {opp.awayTeamName}
                      </Typography>
                      <Chip
                        label={typeof opp.odds === 'string' ? parseFloat(opp.odds).toFixed(2) : opp.odds.toFixed(2)}
                        size="small"
                        sx={{
                          backgroundColor: '#49fbb0',
                          color: '#183530',
                          fontWeight: 700,
                          height: '20px',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {opp.betType} • {opp.betOption}
                      </Typography>
                      <Typography 
                        sx={{ 
                          color: '#49fbb0', 
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <MdTrendingUp size={14} />
                        Confiança: {opp.confidenceScore}%
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>

            {/* Métricas do Bilhete */}
            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <Paper sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    STAKE
                  </Typography>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ticket.stake)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  background: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(76, 175, 80, 0.2)'
                }}>
                  <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.7rem' }}>
                    RETORNO
                  </Typography>
                  <Typography sx={{ color: '#4caf50', fontWeight: 700, fontSize: '0.9rem' }}>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ticket.expectedReturn)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ 
                  p: 1.5, 
                  textAlign: 'center',
                  background: confidenceConfig.bg,
                  borderRadius: '8px',
                  border: `1px solid ${confidenceConfig.color}40`
                }}>
                  <Typography variant="caption" sx={{ color: confidenceConfig.color, fontSize: '0.7rem' }}>
                    CONFIANÇA
                  </Typography>
                  <Typography sx={{ color: confidenceConfig.color, fontWeight: 700, fontSize: '0.9rem' }}>
                    {ticket.confidenceScore}%
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            sx={{
              color: '#49fbb0',
              fontSize: '0.75rem',
              '&:hover': {
                backgroundColor: 'rgba(73, 251, 176, 0.08)',
              },
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}